import as from 'ansis'
import UpdateManager from 'stdout-update'

import { type TableOptions, table } from '@/helpers/std-out/table'

type TitleState = { type: 'title'; content: string }
type LineState = { type: 'line'; content: string }
type LoaderState = { type: 'loader'; content: string }
type TableState = { type: 'table'; content: string[][]; options?: TableOptions }

type UnionStateTypes = TitleState | LineState | LoaderState | TableState

const levelColors = {
  info: (s: string) => s,
  warn: as.yellow,
  error: as.red,
} as const

const levelPrefixes = {
  info: '',
  warn: '[WARN]',
  error: '[ERROR]',
} as const

export const outputFactory =
  (level: 'info' | 'warn' | 'error') =>
  (options?: { animate?: boolean; frameRate?: number }) => {
    // Get the prefix for the logging level
    const titlePrefix = levelPrefixes[level]

    // Colorize the output based on the logging level
    const cl = levelColors[level]

    const manager = UpdateManager.getInstance()

    /**
     * List of all the current log state objects
     *
     * These will be converted into output lines in the update function
     */
    const state: UnionStateTypes[] = []

    let i = 0
    const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏']

    /**
     * Prints the current state to the CLI
     * Based on the logging level a default color may be applied
     */
    function update(complete?: boolean) {
      // Increment the frame index
      i += 1

      manager.update(
        state.map((it) => {
          if (it.type === 'title') {
            return cl(as.bold(`${titlePrefix} ${it.content}`))
          }

          if (it.type === 'loader') {
            return complete
              ? cl(`✔ ${it.content}`)
              : cl(`${frames[i % frames.length]} ${it.content}`)
          }

          if (it.type === 'table') {
            return it.content.map((it) => cl(it.join(''))).join('\n')
          }

          return cl(it.content)
        }),
      )
    }

    // Handle animations
    manager.hook()
    const interval = setInterval(() => {
      update()
    }, options?.frameRate ?? 100)

    /**
     * Executes a final update to the state and then unhooks the manager
     */
    function end() {
      update(true)
      // Force stdout flush to prevent output timing issues
      process.stdout.write('')
      manager.unhook()
      clearInterval(interval)
      return { state }
    }

    return {
      /** Current list of std-out state objects */
      state,
      /** Display a formatted title */
      title: function (title: string) {
        state.push({
          type: 'title',
          content: title,
        })

        return this
      },
      /** Display a new line with the provided content */
      line: function (content?: string) {
        state.push({
          type: 'line',
          content: content ?? '',
        })

        return this
      },
      /** Display a message with a line above and below */
      message: function (content?: string) {
        state.push(
          {
            type: 'line',
            content: '',
          },
          {
            type: 'line',
            content: content ?? '',
          },
          {
            type: 'line',
            content: '',
          },
        )

        return this
      },
      /** Display a loader with a message alongside. */
      loader: function (content?: string) {
        state.push({
          type: 'loader',
          content: content ?? '',
        })

        return this
      },
      /**
       * Update a specific line of a specific type
       * Only works when animate is set to true
       */
      update: function <T extends UnionStateTypes['type']>(
        type: T,
        index: number,
        values: Partial<Omit<Extract<UnionStateTypes, { type: T }>, 'type'>>,
      ) {
        const operation = state.filter((it) => it.type === type)[index]

        if (operation) {
          Object.assign(operation, values)
        }

        // Calculate new table for updated content
        if (operation.type === 'table') {
          table(operation.content, operation.options)
        }

        return this
      },
      /**
       * Delete a specific line of a specific type
       * Only works when the animate is set to true
       */
      delete: function (type: (typeof state)[number]['type'], index: number) {
        let count = 0
        for (let i = 0; i < state.length; i++) {
          if (state[i].type === type) {
            if (count === index) {
              state.splice(i, 1)
              return
            }
            count++
          }
        }

        return this
      },
      /** Display a table that handles padding column widths automatically */
      table: function (data: string[][], opt?: TableOptions) {
        table(data, opt)

        state.push({
          type: 'table',
          content: data,
          options: opt,
        })

        return this
      },
      /** Print the current state to the console */
      print: end,
      /**
       * Finish a log block and unhook from the std-out manager
       * This closes out a running animation
       */
      finish: end,
      /** Exit the process with a success code */
      exit: (code: 'error' | 'success') => {
        end()
        return process.exit(code === 'error' ? 1 : 0)
      },
    }
  }
